import {addBreadcrumb} from '@sentry/browser';
import $ from 'jquery';
import _ from 'underscore';
import * as uuid from 'uuid';

import {standardAnalyticsMetadataToDict} from 'analytics/Util';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, ActionMethod, X_BENCH_ANALYTICS_METADATA} from 'constants/AnalyticsConstants';
import * as context from 'Context';
import {registerHeaderGenerator} from 'util/Net';

import {
  ACTION_CATEGORY,
  ANALYTICS_MODULE,
  AnalyticsEventMetadata,
  NormalizedAnalyticsEventMetadata,
} from './AnalyticsTypes';
import gainsight from './gainsight';

let disabled = false;

function disable(): void {
  disabled = true;
}

function enable(): void {
  disabled = false;
}

// Please reference the analytics tracking guide for implementation best practices :
// https://benchling.quip.com/YzWUAXN4bKfn

interface MetadataWithActionOrigin extends NormalizedAnalyticsEventMetadata {
  action_origin: ActionOrigin;
}

interface MetadataWithActionMethod extends NormalizedAnalyticsEventMetadata {
  action_method: ActionMethod;
}

function isMetadataWithActionOrigin(
  metadata: NormalizedAnalyticsEventMetadata
): metadata is MetadataWithActionOrigin {
  return metadata.hasOwnProperty('action_origin');
}

function isMetadataWithActionMethod(
  metadata: NormalizedAnalyticsEventMetadata
): metadata is MetadataWithActionMethod {
  return metadata.hasOwnProperty('action_method');
}

function convertActionOriginAndActionMethodToLower<T extends AnalyticsEventMetadata>(metadata: T): T {
  if (isMetadataWithActionOrigin(metadata) && typeof metadata.action_origin === 'string') {
    metadata.action_origin = metadata.action_origin.toLowerCase() as ActionOrigin;
  }
  if (isMetadataWithActionMethod(metadata) && typeof metadata.action_method === 'string') {
    metadata.action_method = metadata.action_method.toLowerCase() as ActionMethod;
  }
  return metadata;
}

export function getAnalyticsMetadataHeaders(): {[key: string]: string} {
  return context.get('analyticsMetadata')
    ? {
        [X_BENCH_ANALYTICS_METADATA]: encodeURIComponent(JSON.stringify(context.get('analyticsMetadata'))),
      }
    : {};
}

registerHeaderGenerator(getAnalyticsMetadataHeaders);

export function makeAnalyticsEventId(): string {
  return uuid.v4();
}

function track(module: string, action: string, metadata?: AnalyticsEventMetadata | null): void {
  const processedMetadata =
    metadata instanceof Object
      ? convertActionOriginAndActionMethodToLower(standardAnalyticsMetadataToDict(metadata))
      : metadata;
  if (window.debug) {
    console.debug(`[analytics] ${module}.${action}`, processedMetadata); // oxlint-disable-line no-console
  }

  addBreadcrumb({
    level: 'info',
    category: 'analytics.track',
    message: `${module}.${action}`,
    data: {processedMetadata},
  });

  if (disabled) {
    return;
  }

  $.ajax({
    type: 'POST',
    url: '/1/api/analytics/track',
    contentType: 'application/json',
    data: JSON.stringify({
      module,
      action,
      // eslint-disable-next-line no-restricted-syntax
      metadata: _.extend({client_timestamp: new Date().toISOString()}, processedMetadata),
      url: window.location.href,
      referrer: document.referrer,
      client_api_version: window.clientAPIVersion,
    }),
  });
}

export interface AnalyticsEvent {
  module: ANALYTICS_MODULE;
  action: string;
  actionCategory: ACTION_CATEGORY;
}

/**
 * @deprecated The method should not be used. Use client/analytics/Analytics.ts:track instead
 */
function deprecatedTrackEvent(analyticsEvent: AnalyticsEvent, metadata: AnalyticsEventMetadata = {}): void {
  const processedMetadata = convertActionOriginAndActionMethodToLower(
    standardAnalyticsMetadataToDict(metadata)
  );
  if (window.debug) {
    // oxlint-disable-next-line no-console
    console.debug(`[analytics] ${analyticsEvent.module}.${analyticsEvent.action}`, processedMetadata);
  }

  addBreadcrumb({
    level: 'info',
    category: 'analytics.deprecatedTrackEvent',
    message: `${analyticsEvent.module}.${analyticsEvent.action}`,
    data: processedMetadata,
  });

  if (disabled) {
    return;
  }

  $.ajax({
    type: 'POST',
    url: '/1/api/analytics:track-event',
    contentType: 'application/json',
    data: JSON.stringify({
      analyticsEvent,
      // eslint-disable-next-line no-restricted-syntax
      metadata: _.extend({client_timestamp: new Date().toISOString()}, processedMetadata),
      url: window.location.href,
      referrer: document.referrer,
      clientApiVersion: window.clientAPIVersion,
    }),
  });
}

// Keep in sync with benchling.lib.analytics.deprecated_generate_event_group_id
/**
 * @deprecated The method should not be used. Use client/analytics/Analytics.ts:makeAnalyticsEventId instead
 */
function deprecatedGenerateEventGroupId(tenantId: string, currentUserId: string): string {
  return `${tenantId}-${currentUserId}-${new Date().toISOString()}`;
}

function trackEvent(analyticsEvent: AnalyticsEvent, metadata: AnalyticsEventMetadata = {}): void {
  const processedMetadata = convertActionOriginAndActionMethodToLower(
    standardAnalyticsMetadataToDict(metadata)
  );
  if (window.debug) {
    // oxlint-disable-next-line no-console
    console.debug(`[analytics] ${analyticsEvent.action}`, processedMetadata);
  }
  addBreadcrumb({
    level: 'info',
    category: 'analytics.trackEvent',
    message: `${analyticsEvent.action}`,
    data: processedMetadata,
  });

  if (disabled) {
    return;
  }

  $.ajax({
    type: 'POST',
    url: '/1/api/analytics:track-single-tracked-event',
    contentType: 'application/json',
    data: JSON.stringify({
      analyticsEvent,
      metadata: {...processedMetadata, client_timestamp: new Date().toISOString()},
      url: window.location.href,
      referrer: document.referrer,
      clientApiVersion: window.clientAPIVersion,
    }),
  });

  if (
    BenchlingConfig.ENABLE_GAINSIGHT_PX &&
    gainsight.GAINSIGHT_EVENT_WHITELIST.has(analyticsEvent.action) &&
    window.bootstrapUser != null // Gainsight is only enabled for logged-in users
  ) {
    gainsight.trackCustomEvent(analyticsEvent.action, {
      ...(metadata as Record<string, string | number | boolean | null | undefined>),
      module: analyticsEvent.module,
      actionCategory: analyticsEvent.actionCategory,
    });
  }
}

/**
 * Track an analytics event with the module overriden to the one provided.
 */
function trackEventWithModule(
  analyticsEvent: AnalyticsEvent,
  module: ANALYTICS_MODULE,
  metadata: AnalyticsEventMetadata = {}
): void {
  trackEvent({...analyticsEvent, module}, metadata);
}

export {
  enable,
  disable,
  track,
  deprecatedTrackEvent,
  trackEvent,
  trackEventWithModule,
  deprecatedGenerateEventGroupId,
};
